import {
  Box,
  Button,
  Container,
  Divider,
  Typography,
  Snackbar,
  Alert,
  ThemeProvider,
  AlertColor,
  Fade,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import HeaderBar, { CurrentPages } from '../header-bar/HeaderBar';
import { useEffect, useState } from 'react';
import useActiveOrganisation from '../collaboration/useActiveOrganisation';
import { useTranslation } from 'react-i18next';
import theme from '../resources/NodeTheme';
import EditableTable from '../components/EditableTable';
import PageHeader from '../components/PageHeader';
import GradientBackground from '../resources/components/GradientBackground';

interface Task {
  value: string;
}

const TasksPage = () => {
  const { t } = useTranslation();
  const [activeOrganisation] = useActiveOrganisation();

  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('success');

  // Fetch metadata on component mount
  useEffect(() => {
    const fetchMetadata = async () => {
      if (!activeOrganisation?.organizationId) {
        console.log('No active organization found');
        return;
      }

      try {
        console.log(
          'Fetching metadata for organization:',
          activeOrganisation.organizationId
        );
        const response = await fetch(
          `/api/db/business/metadata/company_tasks`,
          {
            headers: {
              'X-Organization': activeOrganisation.organizationId,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Received metadata:', data);

        if (data && data.value) {
          let tasksArray;
          try {
            // Try to parse the value as JSON first
            const parsedValue = JSON.parse(data.value);
            if (Array.isArray(parsedValue)) {
              tasksArray = parsedValue;
            } else {
              tasksArray = [data.value];
            }
          } catch (e) {
            // If parsing fails, treat it as a string
            tasksArray = [data.value];
          }
          setTasks(tasksArray.map((value) => ({ value })));
        } else {
          console.log('No value found in metadata');
          setTasks([]);
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching metadata:', error);
        setIsLoading(false);
        setSnackbarMessage(`${t('Failed to load tasks data')}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    };

    fetchMetadata();
  }, [activeOrganisation]);

  const handleSave = async () => {
    try {
      // Create an array of task strings
      const value = tasks
        .map((task) => task.value.trim())
        .filter((value) => value !== '');

      const metadataItem = {
        id: 'company_tasks',
        key: 'company_tasks',
        value: JSON.stringify(value),
      };

      await fetch(`/api/db/business/metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization': activeOrganisation.organizationId,
        },
        body: JSON.stringify(metadataItem),
      });

      setSnackbarMessage(t('Tasks saved successfully'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error saving tasks:', error);
      setSnackbarMessage(`${t('Failed to save tasks')}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleAddRow = () => {
    setTasks([...tasks, { value: '' }]);
  };

  return (
    <ThemeProvider theme={theme}>
      <GradientBackground sx={{ minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <HeaderBar currentPage={CurrentPages.Tasks} setCurrentPage={() => {}} />
          <Fade in={!isLoading} timeout={500}>
            <Container maxWidth="md" sx={{ 
              padding: { xs: 2, sm: 3 },
            }}>
              <PageHeader title={t('Tasks')} />
              
              <EditableTable
                title={t('Company Tasks')}
                description={t('Manage your company tasks below. Each row represents a separate task.')}
                instructions={tasks}
                setInstructions={setTasks}
                isLoading={isLoading}
                columnHeader={t('Task')}
              />

              <Box
                sx={{ 
                  display: 'flex', 
                  gap: 2,
                  justifyContent: { xs: 'stretch', sm: 'space-between' },
                }}
              >
                <Button 
                  variant="floating" 
                  startIcon={<AddIcon />} 
                  onClick={handleAddRow} 
                  disabled={isLoading}
                  sx={{ 
                    flex: { xs: 1, sm: 'none' },
                  }}
                >
                  {t('Add Row')}
                </Button>
                <Button 
                  variant="contained" 
                  startIcon={<SaveIcon />}
                  onClick={handleSave} 
                  disabled={isLoading}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  {t('Save Tasks')}
                </Button>
              </Box>

              {/* Snackbar for notifications */}
              <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              >
                <Alert
                  onClose={handleCloseSnackbar}
                  severity={snackbarSeverity}
                  sx={{ width: '100%' }}
                >
                  {snackbarMessage}
                </Alert>
              </Snackbar>
            </Container>
          </Fade>
        </Box>
      </GradientBackground>
    </ThemeProvider>
  );
};

export default TasksPage;
